import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/auth.store';
import { useTranslation } from 'react-i18next';
import TeamMembersTab from './TeamMembersTab';

export default function SettingsPage() {
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState<'profile' | 'establishment' | 'subscription' | 'team'>('profile');
  const [displayName, setDisplayName] = useState('');
  const [establishmentName, setEstablishmentName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [timezone, setTimezone] = useState('Europe/Paris');
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await apiClient.getProfile();
      setDisplayName(result.displayName || result.email.split('@')[0]);
      if (result.establishment) {
        setEstablishmentName(result.establishment.name);
        setAddress(result.establishment.address || '');
        setPhone(result.establishment.phone || '');
        setDescription(result.establishment.description || '');
        setTimezone(result.establishment.timezone || 'Europe/Paris');
      }
      if (result.profilePictureUrl) {
        setProfilePicturePreview(result.profilePictureUrl);
      }
      return result;
    },
    enabled: !!user?.id,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async () => {
      return apiClient.updateProfile({
        displayName,
        establishmentName,
        address,
        phone,
        description,
        timezone,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      if (user) {
        setUser({ ...user, displayName });
      }
      addToast(t('settings.profileUpdated'), 'success');
    },
    onError: (error: any) => {
      addToast(error?.response?.data?.message || t('settings.profileUpdateError'), 'error');
    },
  });

  const uploadProfilePictureMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.uploadProfilePicture(formData);
    },
    onSuccess: (data) => {
      if (data.profilePictureUrl) {
        setProfilePicturePreview(data.profilePictureUrl);
      }
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      addToast(t('settings.photoUploaded'), 'success');
    },
    onError: (error: any) => {
      addToast(error?.response?.data?.message || t('settings.photoUploadError'), 'error');
    },
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => apiClient.getSubscription(),
  });

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      uploadProfilePictureMutation.mutate(file);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div>{t('settings.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 animate-slide-in">
        <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-brand-dark to-primary-600 bg-clip-text text-transparent">{t('settings.title')}</h1>
        <p className="text-neutral-600 mt-2 font-medium">{t('settings.subtitle')}</p>
      </div>

      <div className="mb-8 border-b border-neutral-200 bg-gradient-to-r from-white to-neutral-50 rounded-t-xl">
        <div className="flex gap-4 sm:gap-8 px-4 sm:px-6 overflow-x-auto whitespace-nowrap">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-2 font-bold transition-all duration-300 border-b-2 ${
              activeTab === 'profile'
                ? 'border-gradient-primary text-primary-600 bg-gradient-to-b from-primary-50 to-transparent'
                : 'border-transparent text-neutral-600 hover:text-primary-600'
            }`}
          >
            <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>{t('settings.profileTab')}
          </button>
          <button
            onClick={() => setActiveTab('establishment')}
            className={`py-4 px-2 font-bold transition-all duration-300 border-b-2 ${
              activeTab === 'establishment'
                ? 'border-gradient-primary text-primary-600 bg-gradient-to-b from-primary-50 to-transparent'
                : 'border-transparent text-neutral-600 hover:text-primary-600'
            }`}
          >
            <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>{t('settings.establishmentTab')}
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`py-4 px-2 font-bold transition-all duration-300 border-b-2 ${
              activeTab === 'subscription'
                ? 'border-gradient-primary text-primary-600 bg-gradient-to-b from-primary-50 to-transparent'
                : 'border-transparent text-neutral-600 hover:text-primary-600'
            }`}
          >
            <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>{t('settings.subscriptionTab')}
          </button>
          {user?.role === 'OWNER' && (
            <button
              onClick={() => setActiveTab('team')}
              className={`py-4 px-2 font-bold transition-all duration-300 border-b-2 ${
                activeTab === 'team'
                  ? 'border-gradient-primary text-primary-600 bg-gradient-to-b from-primary-50 to-transparent'
                  : 'border-transparent text-neutral-600 hover:text-primary-600'
              }`}
            >
            <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>{t('settings.teamTab')}
            </button>
          )}
        </div>
      </div>

      <div>
        {activeTab === 'profile' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 sm:p-7 shadow-md hover:shadow-lg transition-all duration-300">
                <h2 className="text-lg font-bold text-neutral-900 mb-6">{t('settings.profileHeading')}</h2>

                <div className="text-center mb-6">
                  <div className="mb-4 flex justify-center">
                    <div className="w-24 h-24 rounded-full bg-gradient-primary flex items-center justify-center overflow-hidden border-4 border-primary-200 shadow-lg">
                      {profilePicturePreview ? (
                        <img
                          src={profilePicturePreview}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-4xl">'¤</div>
                      )}
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    hidden
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadProfilePictureMutation.isPending}
                    className="text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-primary-100 to-primary-50 text-primary-600 hover:from-primary-200 hover:to-primary-100 hover:shadow-md transition-all duration-300 font-medium disabled:opacity-50"
                  >
                    {uploadProfilePictureMutation.isPending ? t('settings.uploading') : t('settings.changePhoto')}
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="pb-4 border-b border-neutral-200">
                    <p className="text-xs font-bold text-neutral-600 uppercase tracking-wider">{t('settings.emailLabel')}</p>
                    <p className="text-neutral-900 mt-2 truncate text-sm font-bold">{profile?.email}</p>
                  </div>

                  <div className="pb-4 border-b border-neutral-200">
                    <p className="text-xs font-bold text-neutral-600 uppercase tracking-wider">{t('settings.roleLabel')}</p>
                    <div className="mt-2 flex items-center">
                      <span className="inline-block px-3 py-1.5 text-xs font-bold rounded-full bg-gradient-success text-white">
                        {profile?.role?.toLowerCase()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-bold text-neutral-600 uppercase tracking-wider">{t('settings.memberSince')}</p>
                    <p className="text-neutral-900 mt-2 text-sm font-bold">
                      {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString(i18n.language) : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 sm:p-7 shadow-md hover:shadow-lg transition-all duration-300">
                <h2 className="text-xl font-bold text-neutral-900 mb-6">{t('settings.personalInfo')}</h2>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-neutral-900 mb-2">{t('settings.displayName')}</label>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm hover:shadow-md transition-all duration-300"
                      placeholder={t('settings.displayNamePlaceholder')}
                    />
                  </div>
                  <button
                    onClick={() => updateProfileMutation.mutate()}
                    disabled={updateProfileMutation.isPending}
                    className="w-full py-3 rounded-lg bg-gradient-primary text-white font-bold hover:shadow-lg hover:shadow-primary-500/30 hover:scale-105 disabled:opacity-50 transition-all duration-300 shadow-md"
                  >
                    {updateProfileMutation.isPending ? t('settings.saving') : t('settings.saveChanges')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'establishment' && (
          <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 sm:p-7 shadow-md hover:shadow-lg transition-all duration-300">
            <h2 className="text-xl font-bold text-neutral-900 mb-6">{t('settings.establishmentDetails')}</h2>
            <div className="space-y-5 max-w-2xl">
              <div>
                <label className="block text-sm font-bold text-neutral-900 mb-2">{t('settings.establishmentName')}</label>
                <input
                  type="text"
                  value={establishmentName}
                  onChange={(e) => setEstablishmentName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm hover:shadow-md transition-all duration-300"
                  placeholder={t('settings.establishmentName')}
                />
                <p className="text-xs text-neutral-600 mt-2 font-medium">{t('settings.establishmentNameHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-neutral-900 mb-2">{t('settings.address')}</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm hover:shadow-md transition-all duration-300"
                  placeholder={t('settings.addressPlaceholder')}
                />
                <p className="text-xs text-neutral-600 mt-2 font-medium">{t('settings.addressHint')}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-neutral-900 mb-2">{t('settings.phone')}</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1 w-full px-4 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('settings.phonePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-900">{t('settings.timezone')}</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="mt-1 w-full px-4 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Europe/Paris">Europe/Paris</option>
                    <option value="Europe/London">Europe/London</option>
                    <option value="Europe/Berlin">Europe/Berlin</option>
                    <option value="US/Eastern">US/Eastern</option>
                    <option value="US/Central">US/Central</option>
                    <option value="US/Pacific">US/Pacific</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-900">{t('settings.description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 w-full px-4 py-2 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={3}
                  placeholder={t('settings.descriptionPlaceholder')}
                />
              </div>

              <button
                onClick={() => updateProfileMutation.mutate()}
                disabled={updateProfileMutation.isPending}
                className="w-full py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {updateProfileMutation.isPending ? t('settings.saving') : t('settings.saveChanges')}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'subscription' && subscription && (
          <div className="bg-white rounded-lg border border-neutral-200 p-4 sm:p-6">
            <h2 className="text-lg font-bold text-neutral-900 mb-4">{t('settings.subscriptionBilling')}</h2>
            <div className="space-y-4 max-w-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-neutral-100">
                <div>
                  <p className="text-sm text-neutral-600 font-medium">{t('settings.statusLabel')}</p>
                  <p className="text-xs text-neutral-500 mt-1">{t('settings.subscriptionStatusHint')}</p>
                </div>
                <span
                  className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${
                    subscription.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800'
                      : subscription.status === 'PAST_DUE'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                  }`}
                >
                  {subscription.status}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-neutral-100">
                <div>
                  <p className="text-sm text-neutral-600 font-medium">{t('settings.periodEnd')}</p>
                  <p className="text-xs text-neutral-500 mt-1">{t('settings.periodEndHint')}</p>
                </div>
                <p className="font-medium text-neutral-900">
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString(i18n.language, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-neutral-100">
                <div>
                  <p className="text-sm text-neutral-600 font-medium">{t('settings.daysRemaining')}</p>
                  <p className="text-xs text-neutral-500 mt-1">{t('settings.daysRemainingHint')}</p>
                </div>
                <p className="font-medium text-neutral-900">
                  {Math.max(
                    0,
                    Math.ceil(
                      (new Date(subscription.currentPeriodEnd).getTime() - new Date().getTime()) /
                        (1000 * 60 * 60 * 24)
                    )
                  )}{' '}
                  {t('settings.daysUnit')}
                </p>
              </div>

              {subscription.stripeCustomerId && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-4 border-b border-neutral-100">
                  <div>
                    <p className="text-sm text-neutral-600 font-medium">{t('settings.paymentProvider')}</p>
                    <p className="text-xs text-neutral-500 mt-1">{t('settings.paymentProviderHint')}</p>
                  </div>
                  <p className="text-sm font-medium text-neutral-900">Stripe</p>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-900">{t('settings.planInfoTitle')}</p>
                <p className="text-xs text-blue-700 mt-2">{t('settings.planInfoDescription')}</p>
              </div>

              {subscription.billingEvents && subscription.billingEvents?.length > 0 && (
                <div className="pt-4 border-t border-neutral-100">
                  <p className="text-sm font-medium text-neutral-600 mb-3">{t('settings.recentBilling')}</p>
                  <div className="space-y-2">
                    {subscription.billingEvents?.slice(0, 5).map((event: any) => (
                      <div
                        key={event.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm border-b border-neutral-100 pb-2"
                      >
                        <div>
                          <p className="text-neutral-600 capitalize">
                            {event.type?.toLowerCase().replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {new Date(event.createdAt).toLocaleDateString(i18n.language)}
                          </p>
                        </div>
                        {event.amount && (
                          <p className="font-medium text-neutral-900">
                            €{(event.amount / 100).toFixed(2)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 space-y-3">
                {subscription.status === 'ACTIVE' && subscription.stripeCustomerId ? (
                  <button
                    onClick={async () => {
                      try {
                        const { url } = await apiClient.createCustomerPortalSession(
                          `${window.location.origin}/settings?tab=subscription`
                        );
                        window.location.href = url;
                      } catch {
                        addToast(t('settings.billingPortalError'), 'error');
                      }
                    }}
                    className="w-full py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
                  >
                    {t('settings.manageSubscription')}
                  </button>
                ) : subscription.status !== 'ACTIVE' ? (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="w-full py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors"
                  >
                    {t('settings.subscribe')}
                  </button>
                ) : null}
                <div className="p-4 bg-neutral-50 rounded-lg">
                  <p className="text-xs text-neutral-600">{t('settings.billingHelp')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'team' && user?.role === 'OWNER' && <TeamMembersTab />}
      </div>
    </div>
  );
}
